/**
 * MacroTargeting Component
 * Allows users to set specific macro nutrient targets
 */

import React, { useState, useEffect } from "react";
import { LLMMacros } from "../types/common";
import { NumberInput } from "./NumberInput";

interface MacroTarget {
    enabled: boolean;
    value: number;
}

interface MacroTargetingProps {
    onChange?: (targets: Partial<LLMMacros>) => void;
    initialValues?: Partial<LLMMacros>;
    className?: string;
    style?: React.CSSProperties;
}

export const MacroTargeting: React.FC<MacroTargetingProps> = ({
    onChange,
    initialValues,
    className,
    style,
}) => {
    const [macroTargets, setMacroTargets] = useState<
        Record<keyof LLMMacros, MacroTarget>
    >({
        calories: { 
            enabled: initialValues?.calories !== undefined, 
            value: initialValues?.calories || 2000 
        },
        protein: { 
            enabled: initialValues?.protein !== undefined, 
            value: initialValues?.protein || 50 
        },
        fiber: { 
            enabled: initialValues?.fiber !== undefined, 
            value: initialValues?.fiber || 25 
        },
        carbs: { 
            enabled: initialValues?.carbs !== undefined, 
            value: initialValues?.carbs || 250 
        },
        fat: { 
            enabled: initialValues?.fat !== undefined, 
            value: initialValues?.fat || 65 
        },
    });

    // Notify parent of changes
    useEffect(() => {
        const enabledTargets: Partial<LLMMacros> = {};
        Object.entries(macroTargets).forEach(([key, target]) => {
            if (target.enabled) {
                enabledTargets[key as keyof LLMMacros] = target.value;
            }
        });
        onChange?.(enabledTargets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [macroTargets]); // Removed onChange to prevent infinite loop

    const handleToggle = (macro: keyof LLMMacros) => {
        setMacroTargets((prev) => ({
            ...prev,
            [macro]: {
                ...prev[macro],
                enabled: !prev[macro].enabled,
            },
        }));
    };

    const handleValueChange = (macro: keyof LLMMacros, value: number) => {
        setMacroTargets((prev) => ({
            ...prev,
            [macro]: {
                ...prev[macro],
                value: value,
            },
        }));
    };

    const getUnit = (macro: keyof LLMMacros): string => {
        switch (macro) {
            case "calories":
                return "kcal";
            case "protein":
            case "fiber":
            case "carbs":
            case "fat":
                return "g";
            default:
                return "";
        }
    };

    const getMacroInfo = (macro: keyof LLMMacros): { label: string; description: string; icon: string } => {
        switch (macro) {
            case "calories":
                return { 
                    label: "Calories", 
                    description: "Total daily energy intake",
                    icon: "🔥"
                };
            case "protein":
                return { 
                    label: "Protein", 
                    description: "For muscle maintenance and growth",
                    icon: "💪"
                };
            case "carbs":
                return { 
                    label: "Carbohydrates", 
                    description: "Primary energy source",
                    icon: "🌾"
                };
            case "fat":
                return { 
                    label: "Fat", 
                    description: "Essential for hormone production",
                    icon: "🥑"
                };
            case "fiber":
                return { 
                    label: "Fiber", 
                    description: "For digestive health",
                    icon: "🥦"
                };
            default:
                return { label: macro, description: "", icon: "" };
        }
    };

    return (
        <div className={className} style={{ ...style }}>
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                gap: '16px' 
            }}>
                {(Object.keys(macroTargets) as Array<keyof LLMMacros>).map((macro) => {
                    const info = getMacroInfo(macro);
                    const target = macroTargets[macro];
                    
                    return (
                        <div
                            key={macro}
                            style={{
                                backgroundColor: target.enabled ? '#eff6ff' : 'white',
                                border: `2px solid ${target.enabled ? '#3b82f6' : '#e2e8f0'}`,
                                borderRadius: '8px',
                                padding: '16px',
                                transition: 'all 0.2s',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                                <span style={{ fontSize: '24px', marginRight: '12px' }}>{info.icon}</span>
                                <div style={{ flex: 1 }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between',
                                        marginBottom: '4px'
                                    }}>
                                        <h5 style={{ 
                                            fontSize: '16px', 
                                            fontWeight: 600, 
                                            color: '#1e293b',
                                            margin: 0
                                        }}>
                                            {info.label}
                                        </h5>
                                        <label style={{
                                            position: 'relative',
                                            display: 'inline-block',
                                            width: '44px',
                                            height: '24px',
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={target.enabled}
                                                onChange={() => handleToggle(macro)}
                                                style={{ opacity: 0, width: 0, height: 0 }}
                                            />
                                            <span style={{
                                                position: 'absolute',
                                                cursor: 'pointer',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: target.enabled ? '#3b82f6' : '#cbd5e1',
                                                transition: '0.3s',
                                                borderRadius: '24px',
                                            }}>
                                                <span style={{
                                                    position: 'absolute',
                                                    content: '',
                                                    height: '18px',
                                                    width: '18px',
                                                    left: target.enabled ? '22px' : '3px',
                                                    bottom: '3px',
                                                    backgroundColor: 'white',
                                                    transition: '0.3s',
                                                    borderRadius: '50%',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                }} />
                                            </span>
                                        </label>
                                    </div>
                                    <p style={{ 
                                        fontSize: '12px', 
                                        color: '#64748b',
                                        margin: 0
                                    }}>
                                        {info.description}
                                    </p>
                                </div>
                            </div>
                            
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <NumberInput
                                    value={target.value}
                                    min={0}
                                    onChange={(val) => handleValueChange(macro, Math.max(0, val || 0))}
                                    disabled={!target.enabled}
                                />
                                <span style={{ 
                                    fontSize: '14px', 
                                    color: '#64748b',
                                    fontWeight: 500,
                                    minWidth: '30px'
                                }}>
                                    {getUnit(macro)}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
            
            <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#0369a1',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <span>💡</span>
                <span>Enable specific macro targets to ensure your meal plan meets your nutritional goals. The AI will optimize meals to hit these targets.</span>
            </div>
        </div>
    );
};
